import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { UploadCloud, Loader2, CheckCircle2, AlertTriangle, FileText } from 'lucide-react';
import { poDocumentsAPI } from '../../services/api';
import { toast } from 'sonner';

// Shared PO document upload + Gemini 3.1 Pro extraction panel — used at PO creation time only.
// Extracted fields are surfaced back to the parent form for review/edit before save.
export const POExtractionPanel = ({ poType = 'delivery', onExtracted, initialFilename = '' }) => {
  const [status, setStatus] = useState('idle'); // idle | uploading | done | failed
  const [fileName, setFileName] = useState(initialFilename);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setStatus('uploading');
    try {
      const result = await poDocumentsAPI.extract(file, poType);
      onExtracted(result);
      if (result.extraction_status === 'completed' && result.extracted) {
        setStatus('done');
        toast.success('PO document extracted — review the fields below before saving');
      } else {
        setStatus('failed');
        toast.error(result.extraction_error || 'AI extraction failed — please fill fields manually');
      }
    } catch (err) {
      setStatus('failed');
      toast.error(err.response?.data?.detail || 'Failed to upload/extract document');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="border border-dashed rounded-lg p-3 bg-muted/20 space-y-2" data-testid="po-extraction-panel">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">PO Document (optional)</Label>
        {status === 'uploading' && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" data-testid="po-extraction-loading" />}
        {status === 'done' && <CheckCircle2 className="w-4 h-4 text-green-600" data-testid="po-extraction-success-icon" />}
        {status === 'failed' && <AlertTriangle className="w-4 h-4 text-amber-600" data-testid="po-extraction-failed-icon" />}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={handleFile}
        data-testid="po-document-file-input"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={status === 'uploading'}
        data-testid="upload-po-document-btn"
      >
        <UploadCloud className="w-3.5 h-3.5 mr-2" />
        {status === 'uploading' ? 'Extracting with AI…' : fileName ? 'Replace document' : 'Upload PDF/Image & Extract with AI'}
      </Button>
      {fileName && (
        <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="po-document-filename">
          <FileText className="w-3 h-3" />{fileName}
        </p>
      )}
      <p className="text-xs text-muted-foreground">Gemini AI reads the PO document and pre-fills the fields below — review and edit before saving.</p>
    </div>
  );
};

export default POExtractionPanel;
